import * as React from 'react';
import {ChangeEvent, useEffect, useState} from 'react';
import {faAngleDown, faAngleUp} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import {GaItemListName} from '../../../client/ga/ga-ecommerce.functions';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {ImpError} from '../../../client/imp-error/imp-error.class';
import {ItemCards} from '../ItemCard/ItemCards';
import {ItemsService} from '../../../client/items/items.service';
import {ItemToAdd} from '../../order-items/order-items.class';
import {ListsWorkflow} from '../../../client/lists/lists.workflow';
import {OrderableItem, ShareItemRes} from '../item.class';
import {OrderItemsWorkflow} from '../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../client/orders/orders.service';
import {ShareLinkModal} from '../../share-link/ShareLinkModal';
import {ShareLinkParams} from '../../share-link/share-link.class';
import {UserStateService} from '../../../client/users/user-state.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import {PageHeader} from '../../ui/PageHeader/PageHeader';
import {EmptyState} from '../../ui/EmptyState/EmptyState';
import {CheckBox} from '../../ui/forms/CheckBox/CheckBox';
import Button from '../../ui/Buttons/Button';

const SHOW_MORE_LIMIT = 5;

interface RequiredItem extends OrderableItem {
    hidden?: boolean;
    qtyLimit?: number;
}

interface RequiredItemsProps {
    disableRecaptcha: boolean;
    onError: (newErrorMessage: string) => void;
    requiredItems: RequiredItem[];
    user: User;
}

export const RequiredItems = ({disableRecaptcha, onError, requiredItems, user}: RequiredItemsProps) => {
    const [hasCheckedItems, setHasCheckedItems] = useState<boolean>(false);
    const [showMore, setShowMore] = useState<boolean>(requiredItems.length > SHOW_MORE_LIMIT);
    const componentName = `RequiredItems`;
    const gaItemListName: GaItemListName = `required_items`;
    const listsWorkflow: ListsWorkflow = useService(`listsWorkflow`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const userStateService: UserStateService = useService(`userStateService`);

    // TODO eventually find a way to reduce this shareLink modal duplicated code
    const [shareLinkErr, setShareLinkErr] = useState(``);
    const [shareLinkModal, setShareLinkModal] = useState({
        linkId: ``,
        linkType: undefined,
        show: false,
    });
    const [shareLinkModalTitle, setShareLinkModalTitle] = useState(undefined);
    const [shareLinkRes, setShareLinkRes] = useState<ShareItemRes>(null);
    const [shareLinkShowForm, setShareLinkShowForm] = useState(true);
    const [shareLinkShowSuccess, setShareLinkShowSuccess] = useState(false);
    const [shareLinkShowWarning, setShareLinkShowWarning] = useState(false);
    const itemsService: ItemsService = useService(`itemsService`);
    const ordersService: OrdersService = useService(`ordersService`);

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            addToOrder(userStateService.addToOrderItems);
        } else if (userStateService.hasPendingAddToList(componentName)) {
            listsWorkflow.addToList(
                userStateService.addToListAction,
                userStateService.addToListItems,
                componentName,
                gaItemListName,
                userStateService.addToListItemDesc,
                userStateService.addToListItemImage,
            );
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Adds items to order
     */
    const addItemsToOrder = () => {
        const itemsToAdd = buildItemAddArray();
        gaLegacyCustomEvent({eventAction: `Add All Items to Order Clicks`, eventCategory: `Ecommerce`, eventLabel: gaItemListName});
        addToOrder(itemsToAdd);
    };

    /**
     * Add items to order
     * @param itemsToAdd - Items to add to the order
     * @private
     */
    const addToOrder = (itemsToAdd: ItemToAdd[]) => {
        // Check itemsToAdd for errors
        let invalidItemNum = ``;
        let error = ``;
        for (const itemToAdd of itemsToAdd) {
            if (itemToAdd.error) {
                error = itemToAdd.error;
                invalidItemNum = itemToAdd.item;
                break;
            }
        }

        onError(error);

        // Scroll to highlight first error
        if (invalidItemNum) {
            document.getElementById(`itemCard_${invalidItemNum}`).scrollIntoView();
        } else {
            orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);
        }
    };

    /**
     * Gathers items and returns itemToAddArray
     */
    const buildItemAddArray = (): ItemToAdd[] => {
        const itemsToAddArray: ItemToAdd[] = [];
        for (const requiredItem of requiredItemsState) {
            if (requiredItem.checked) {
                let error = ``;
                const unitsOrdered = requiredItem.unitsOrdered;
                if (!unitsOrdered || requiredItem.qtyLimit < unitsOrdered) {
                    error = `Please enter a valid quantity for item(s) below.`;
                }
                itemsToAddArray.push({
                    error,
                    gaEcommerce: {item_list_name: gaItemListName},
                    item: requiredItem.item,
                    unitsOrdered,
                });
            }
        }
        return itemsToAddArray;
    };

    /**
     * Updates all visible item checkboxes to match select all checkbox
     * @param e - Change event
     */
    const checkAllItemBoxes = (e: ChangeEvent<HTMLInputElement>) => {
        if (showMore) {
            toggleHidden();
        }
        const updatedRequiredItems = [...requiredItemsState];
        for (const requiredItem of updatedRequiredItems) {
            if (!requiredItem.hidden) {
                requiredItem.checked = e.target.checked;
            }
        }
        setHasCheckedItems(e.target.checked);
        setRequiredItemsState(updatedRequiredItems);
    };

    /**
     * Resets ShareLinkModal state on close
     */
    const handleCloseShareLinkModal = () => {
        // Close modal and reset state
        setShareLinkModal({
            linkId: ``,
            linkType: undefined,
            show: false,
        });

        // Reset other modal state to default values
        setShareLinkErr(``);
        setShareLinkModalTitle(undefined);
        setShareLinkRes(null);
        setShareLinkShowForm(true);
        setShareLinkShowSuccess(false);
        setShareLinkShowWarning(false);
    };

    // Add additional attributes to RequiredItems
    const mapRequiredItems = (orderableItems: OrderableItem[]): RequiredItem[] => {
        return orderableItems.map((orderableItem, index) => {
            return {
                unitsOrdered: 1,
                hidden: index > SHOW_MORE_LIMIT - 1,
                ...orderableItem,
            };
        });
    };
    const [requiredItemsState, setRequiredItemsState] = useState<RequiredItem[]>(mapRequiredItems(requiredItems));

    /**
     * Records item quantity
     * @param itemNum
     * @param unitsOrdered
     */
    const onQtyUpdate = (itemNum: string, unitsOrdered: number) => {
        const updatedListItemItems = requiredItemsState.map((listItems) => {
            if (itemNum === listItems.item) {
                return {...listItems, unitsOrdered};
            }
            return listItems;
        });
        setRequiredItemsState(updatedListItemItems);
    };

    /**
     * Records item being checked
     * @param id - Item to evaluate
     * @param checked - Checked state
     */
    const recordItemCheckbox = (id: string, checked: boolean) => {
        let nowHasCheckedItems = checked;
        const updatedListItemItems = requiredItemsState.map((listItem) => {
            if (id === listItem.item) {
                return {...listItem, checked};
            }
            if (listItem.checked) {
                nowHasCheckedItems = true;
            }
            return listItem;
        });
        setHasCheckedItems(nowHasCheckedItems);
        setRequiredItemsState(updatedListItemItems);
    };

    /**
     * Shares items from order as a list
     * @param shareLinkParams
     */
    const shareLink = (shareLinkParams: ShareLinkParams) => {
        setShareLinkErr(``);

        // Determine share type
        let shareLinkPromise;
        if (shareLinkParams.type === `items`) {
            shareLinkPromise = ordersService.shareOrder(shareLinkParams);
        } else {
            shareLinkPromise = itemsService.shareItem(shareLinkParams);
        }

        // Handle response
        shareLinkPromise
            .then((shareItemRes: ShareItemRes) => {
                setShareLinkRes(shareItemRes);
                if (shareItemRes.result === `OK`) {
                    setShareLinkModalTitle(`Thank you`);
                    setShareLinkShowSuccess(true);
                    setShareLinkShowForm(false);
                } else if (shareItemRes.result === `WARNING`) {
                    setShareLinkModalTitle(`Error`);
                    setShareLinkShowWarning(true);
                    setShareLinkShowSuccess(false);
                    setShareLinkShowForm(false);
                }
            })
            .catch((shareLinkErrParam: ImpError) => {
                setShareLinkErr(shareLinkErrParam.message);
            });
    };

    /**
     * TBD
     */
    const toggleHidden = () => {
        if (showMore) {
            setRequiredItemsState(
                requiredItemsState.map((orderableItem) => {
                    orderableItem.hidden = false;
                    return orderableItem;
                }),
            );
        } else {
            setRequiredItemsState(
                requiredItemsState.map((orderableItem, index) => {
                    orderableItem.hidden = index > SHOW_MORE_LIMIT - 1;
                    return orderableItem;
                }),
            );
        }
        setShowMore(!showMore);
    };

    /**
     * Template
     */
    if (!requiredItems) {
        return <></>;
    }
    return (
        <>
            <div data-e2e="requiredItemText">
                <PageHeader
                    title="Required Items"
                    type="h4"
                />
            </div>
            <div className="tw-mb-4">Items below are required. They are not included and need to be ordered separately.</div>
            <div className="tw-bg-gray-100 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-12 tw-mb-0 tw-items-center">
                    <div className="tw-w-full tw-col-span-1 md:tw-col-span-7">
                        <CheckBox
                            alias="selectAll"
                            className="!tw-mb-0"
                            label="Select All"
                            name="selectAll"
                            noMargin={true}
                            onChange={(e) => checkAllItemBoxes(e)}
                        />
                    </div>
                    <div
                        className={`tw-col-span-1 md:tw-col-span-5 tw-text-right${
                            hasCheckedItems
                                ? ' tw-fixed tw-right-0 tw-bottom-0 tw-left-0 tw-z-[1030] md:tw-relative md:tw-right-auto md:tw-bottom-auto md:tw-left-auto '
                                : ' tw-hidden'
                        } tw-bg-gray-100 tw-p-4 tw-pb-6 md:tw-p-0 md:tw-block `}
                    >
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-justify-end tw-m-0">
                            <div className="tw-w-full tw-col-start-1 tw-col-span-2 md:tw-col-start-3 md:tw-col-span-2 tw-order-[0] md:tw-order-1">
                                <Button
                                    aria-label="Required Items Add to Order"
                                    className="tw-mt-0 tw-w-full"
                                    e2e="addToOrderLg"
                                    onClick={() => addItemsToOrder()}
                                    size="sm"
                                    type="button"
                                    variant="primary"
                                >
                                    Add to Order
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {requiredItemsState?.length > 0 ? (
                <ItemCards
                    cardOptions={{
                        addToList: true,
                        canEdit: false,
                        categoryLink: true,
                        contextMenu: true,
                        inventoryControls: false,
                        minVal: 0,
                        remove: false,
                        showInventoryControls: false,
                        showLastPurchased: false,
                        showShareLink: true,
                    }}
                    componentName={componentName}
                    gaItemListName={gaItemListName}
                    items={requiredItemsState}
                    onStepperUpdate={onQtyUpdate}
                    onStepperUpdateAllowInvalid={true}
                    recordItemCheckbox={recordItemCheckbox}
                    setShareLinkModal={setShareLinkModal}
                    user={user}
                />
            ) : (
                <EmptyState message="List Contains No Items" />
            )}
            <div className="tw-grid tw-grid-cols-1">
                {requiredItemsState.length > SHOW_MORE_LIMIT && showMore && (
                    <div
                        className="tw-relative tw-w-full tw-mb-4"
                        onClick={toggleHidden}
                    >
                        <Button
                            className="tw-w-full !tw-bg-white"
                            size="sm"
                            variant="light"
                        >
                            View All Items{' '}
                            <FontAwesomeIcon
                                icon={faAngleDown}
                                className="tw-ml-2"
                            />
                        </Button>
                    </div>
                )}
                {requiredItemsState.length > SHOW_MORE_LIMIT && !showMore && (
                    <div
                        className="tw-w-full tw-relative tw-mb-4"
                        onClick={toggleHidden}
                    >
                        <Button
                            className="tw-w-full !tw-bg-white"
                            size="sm"
                            variant="light"
                        >
                            View Fewer Items{' '}
                            <FontAwesomeIcon
                                icon={faAngleUp}
                                className="tw-ml-2"
                            />
                        </Button>
                    </div>
                )}
            </div>
            <ShareLinkModal
                disableRecaptcha={disableRecaptcha}
                linkId={shareLinkModal.linkId}
                onClose={handleCloseShareLinkModal}
                onShareLink={shareLink}
                shareLinkErr={shareLinkErr}
                shareLinkModalTitle={shareLinkModalTitle}
                shareLinkRes={shareLinkRes}
                shareLinkShowForm={shareLinkShowForm}
                shareLinkShowSuccess={shareLinkShowSuccess}
                shareLinkShowWarning={shareLinkShowWarning}
                shareLinkType={shareLinkModal.linkType}
                show={shareLinkModal.show}
                singleName={shareLinkModal.linkType === `item`}
                user={user}
            />
        </>
    );
};
